'use client'

import { memo, type ReactNode } from 'react'
import { Handle, Position } from '@xyflow/react'
import { cn } from '@/lib/utils'

export interface BaseNodeProps {
  children: ReactNode
  icon: ReactNode
  label: string
  selected?: boolean
  hasInputHandle?: boolean
  hasOutputHandle?: boolean
  outputHandles?: Array<{
    id: string
    label: string
    type: string
  }>
  className?: string
}

function BaseNodeComponent({
  children,
  icon,
  label,
  selected,
  hasInputHandle = true,
  hasOutputHandle = true,
  outputHandles,
  className,
}: BaseNodeProps) {
  return (
    <div
      className={cn(
        'min-w-[200px] rounded-lg border bg-card text-card-foreground shadow-sm',
        selected && 'ring-2 ring-primary',
        className
      )}
    >
      {/* Input Handle */}
      {hasInputHandle && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-3 !h-3 !bg-muted-foreground !border-2 !border-background"
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 rounded-t-lg">
        <span className="text-muted-foreground">{icon}</span>
        <span className="font-medium text-sm">{label}</span>
      </div>

      {/* Content */}
      <div className="p-3">
        {children}
      </div>

      {/* Output Handles */}
      {hasOutputHandle && !outputHandles && (
        <Handle
          type="source"
          position={Position.Right}
          className="!w-3 !h-3 !bg-primary !border-2 !border-background"
        />
      )}

      {/* Multiple Output Handles */}
      {outputHandles && outputHandles.length > 0 && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex flex-col gap-4">
          {outputHandles.map((handle, index) => (
            <div key={handle.id} className="relative flex items-center">
              <span className="absolute right-4 text-xs text-muted-foreground whitespace-nowrap">
                {handle.label}
              </span>
              <Handle
                type="source"
                position={Position.Right}
                id={handle.id}
                className="!w-3 !h-3 !bg-primary !border-2 !border-background"
                style={{
                  position: 'relative',
                  top: 0,
                  transform: 'none',
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export const BaseNode = memo(BaseNodeComponent)
